import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { fmt } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

function sessionMonths(session) {
  const names = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
  let start = new Date().getFullYear();
  if (session && /^\d{4}/.test(session)) start = parseInt(session.slice(0, 4), 10);
  return names.map((mm, i) => `${mm} ${i < 9 ? start : start + 1}`);
}

export default function AssignTransportDialog({ open, onOpenChange, student, onAssigned }) {
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [tRoute, setTRoute] = useState("");
  const [tVehicle, setTVehicle] = useState("");
  const [tPickup, setTPickup] = useState("");
  const [tFee, setTFee] = useState(0);
  const [tMonths, setTMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const months = sessionMonths(student?.academic_session);
  const pickups = (routes.find((r) => r.id === tRoute)?.pickup_points) || [];

  useEffect(() => {
    if (!open || !student) return;
    setLoading(true);
    (async () => {
      try {
        const [r, v, a] = await Promise.all([
          api.get("/routes"),
          api.get("/vehicles"),
          api.get("/transport/assignments"),
        ]);
        setRoutes(r.data);
        setVehicles(v.data);
        const existing = (a.data || []).find((x) => x.student_id === student.id);
        if (existing) {
          setTRoute(existing.route_id || "");
          setTVehicle(existing.vehicle_id || "");
          setTPickup(existing.pickup_point || "");
          setTFee(existing.transport_fee || 0);
          setTMonths(existing.transport_months || []);
        } else {
          setTRoute(""); setTVehicle(""); setTPickup(""); setTFee(0); setTMonths([]);
        }
      } catch {
        setRoutes([]); setVehicles([]);
      } finally { setLoading(false); }
    })();
  }, [open, student]);

  const save = async () => {
    if (!tRoute) { toast.error("Select a route"); return; }
    setSaving(true);
    try {
      await api.post("/transport/assign", {
        student_id: student.id, route_id: tRoute, pickup_point: tPickup,
        vehicle_id: tVehicle, transport_fee: Number(tFee || 0), transport_months: tMonths,
      });
      toast.success("Transport saved");
      onAssigned?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0 gap-0" data-testid="student-assign-transport-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0"><DialogTitle>Add/Edit Transport{student ? ` — ${student.name}` : ""}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>
        ) : (
          <div className="space-y-3 overflow-y-auto px-6 flex-1 pb-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Route</Label>
                <Select value={tRoute} onValueChange={(v) => { const r = routes.find((x) => x.id === v); setTRoute(v); if (!tFee || tFee === 0) setTFee(r?.monthly_fee || 0); if (!(r?.pickup_points || []).includes(tPickup)) setTPickup(""); }}>
                  <SelectTrigger data-testid="st-route"><SelectValue placeholder="Select route" /></SelectTrigger>
                  <SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle</Label>
                <Select value={tVehicle} onValueChange={setTVehicle}>
                  <SelectTrigger data-testid="st-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.vehicle_name || v.vehicle_number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Pickup Point</Label>
                <Select value={tPickup} onValueChange={setTPickup} disabled={!tRoute || pickups.length === 0}>
                  <SelectTrigger data-testid="st-pickup"><SelectValue placeholder={!tRoute ? "Select a route first" : (pickups.length ? "Select pickup point" : "No pickup points for this route")} /></SelectTrigger>
                  <SelectContent>{pickups.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Transport Fee (monthly)</Label><Input type="number" value={tFee} onChange={(e) => setTFee(e.target.value)} data-testid="st-fee" /></div>
            </div>
            <div>
              <Label>Transport Months <span className="text-xs text-slate-400">({tMonths.length || "all 12"})</span></Label>
              <label className="flex items-center gap-2 mt-1 mb-1.5 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={tMonths.length === months.length} onChange={(e) => setTMonths(e.target.checked ? months : [])} data-testid="st-select-all-months" />
                Select All Months
              </label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {months.map((m) => {
                  const on = tMonths.includes(m);
                  return <button key={m} type="button" onClick={() => setTMonths(on ? tMonths.filter((x) => x !== m) : [...tMonths, m])} className={`px-2 py-1.5 rounded-md text-xs font-medium border ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`} data-testid={`st-month-${m.replace(" ", "-")}`}>{m}</button>;
                })}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="px-6 py-4 border-t shrink-0"><Button onClick={save} disabled={saving || loading} data-testid="st-save">{saving ? <Loader2 className="animate-spin" /> : "Save Transport"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
